import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import styled from "styled-components";

import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import ActionFormButton from "../../../common/ActionFormButton";
import ChangePassword from "./components/ChangePassword";

import {
  handleRepeatedFormValues,
  renderRowWithTwoItems,
  renderSectionTitle,
  getRowItemObject,
  Section,
  Wrapper,
  Row,
  renderRowWithSelect,
  renderRowWithSingleItem,
  renderRowFileButton,
  renderRowWithSingleCheckbox,
  RowItem,
} from "../../../common/FormUtils";
import Roles from "../../../../types/Roles";
import Event, { DayOfWeek, EventCategory } from "../../../../types/Event";
import DatePicker from "react-datetime-picker";
import Store from "../../../../types/Store";
import _, { values } from "lodash";
import Checkbox from "../../../common/Checkbox";
import DaysOfWeek from "../../../common/DaysOfWeek";
import ImageUpload from "../../../common/ImageUpload";
import DB_PATH from "../../../../database/DB_PATH";
import { fetchEventLinks } from "../../../../database/events/eventLinks";
import {
  guessTimeZone,
  listTimeZones,
  utcIsoToZonedWallTime,
  zonedWallTimeToUtcIso,
} from "../../../../utils/eventTimeZone";

const EditPasswordButtonWrapper = styled.div`
  display: block;
`;

const GatedNotice = styled.div`
  margin: 0 0 16px 0;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #f0c36d;
  background: #fdf6e3;
  color: #6b5320;
  font-size: 14px;
  line-height: 1.4;
`;

const LinksLoadNotice = styled.div`
  margin: -8px 0 16px 0;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #d6d6d6;
  background: #f6f6f6;
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.4;
`;

// An event is gated when it requires an entitlement. The gating itself is
// authored through the MCP, so this form only reads it.
const isEventGated = (values: any): boolean =>
  (Array.isArray(values?.requiredEntitlementIds) &&
    values.requiredEntitlementIds.length > 0) ||
  Boolean(values?.hasProtectedMeetingURL) ||
  Boolean(values?.hasProtectedReplayURL);

const coerceToDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      const parsed = value.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof value.seconds === "number") {
      const parsed = new Date(value.seconds * 1000);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
};

// The zone list is the same for every render, so it is built once. Shaped for
// renderRowWithSelect, which reads option.id and option.name.
const TIME_ZONE_OPTIONS: Array<{ id: string; name: string }> = listTimeZones().map(
  (zone) => ({ id: zone, name: zone })
);

// The empty first option. An event with no zone must be able to stay that way,
// so "not set" is a real choice and not a missing one. The label names what
// actually happens then: the email falls back to US Eastern. In-app screens
// always render in the reader's device zone, with or without this field.
const NO_TIME_ZONE_OPTION = {
  id: "",
  name: "Not set (emails fall back to US Eastern)",
};

// A stored zone the browser does not list (an alias such as Asia/Calcutta, or
// anything the MCP wrote) still has to show as the selected value.
const timeZoneOptionsFor = (
  stored: any
): Array<{ id: string; name: string }> => {
  const value = typeof stored === "string" ? stored.trim() : "";
  const known =
    !value || TIME_ZONE_OPTIONS.some((option) => option.id === value);
  return known
    ? [NO_TIME_ZONE_OPTION, ...TIME_ZONE_OPTIONS]
    : [NO_TIME_ZONE_OPTION, { id: value, name: value }, ...TIME_ZONE_OPTIONS];
};

// The zone stored on the event, or "" when it has none. Never guessed: a guess
// would stamp the editor's clock onto an event the coach set somewhere else.
const storedFormTimeZone = (value: any): string =>
  typeof value === "string" ? value.trim() : "";

type Props = {
  touched: any;
  setFieldValue: Function;
  onChageFormToEditMode: Function;
  onEditPassword: Function;
  handleSubmit: Function;
  onRemoveItem: Function;
  isSubmitting: boolean;
  item: any;
  mode: string;
  errors: any;
  values: any;
};

type EventFormValues = Omit<Event, "startTime" | "endTime"> & {
  startTime: Date | string | null;
  endTime: Date | string | null;
  // UI only, stripped before the payload leaves the client. False means the two
  // URL inputs never received the stored values, so they must not be sent.
  protectedLinksLoaded: boolean;
};

type LinksLoadState = "idle" | "loading" | "loaded" | "failed";

type State = {
  isPasswordDialogOpen: boolean;
  isRecurringEvent: boolean;
  linksLoadState: LinksLoadState;
};

class UserForm extends Component<Props, State> {
  state: State = {
    isPasswordDialogOpen: false,
    isRecurringEvent: false,
    linksLoadState: "idle",
  };

  // A gated event's real links are not on the public doc, so the snapshot the
  // list passes in cannot prefill them. Ask the callable for the stored values.
  // Until that call succeeds, protectedLinksLoaded stays false and the database
  // layer drops both URL fields from the payload. An empty box must never be
  // read as a deliberate clear.
  componentDidMount() {
    const { values, setFieldValue } = this.props;
    if (!values?.id || !isEventGated(values)) return;

    this.setState({ linksLoadState: "loading" });
    fetchEventLinks({ eventId: values.id })
      .then((links) => {
        const { touched, values: current } = this.props;
        // Do not overwrite a value the admin typed while the call was in
        // flight. Formik only marks a field touched on blur, so a box the
        // admin is still typing in reports untouched. Check the value too.
        if (links.meetingURL && !touched?.meetingURL && !current.meetingURL) {
          setFieldValue("meetingURL", links.meetingURL);
        }
        if (links.replayURL && !touched?.replayURL && !current.replayURL) {
          setFieldValue("replayURL", links.replayURL);
        }
        setFieldValue("protectedLinksLoaded", true);
        this.setState({ linksLoadState: "loaded" });
      })
      .catch((error) => {
        console.error("Could not load the private event links:", error);
        this.setState({ linksLoadState: "failed" });
      });
  }

  onTogglePassowordDialog = (): void => {
    const { isPasswordDialogOpen } = this.state;

    this.setState({
      isPasswordDialogOpen: !isPasswordDialogOpen,
    });
  };

  onChangePassword = (newPassword: string): void => {
    const { onEditPassword } = this.props;

    this.setState(
      {
        isPasswordDialogOpen: false,
      },
      () => onEditPassword(newPassword)
    );
  };

  renderPasswordSection = (): Object => {
    const passwordConfirmInputFieldData = getRowItemObject(
      "Repeat the Password",
      "Enter the Password again.",
      "password",
      "passwordConfirm"
    );
    const passwordInputFieldData = getRowItemObject(
      "Password",
      "Enter the Password",
      "password",
      "password"
    );

    return (
      <Section>
        {renderSectionTitle("Password")}
        {renderRowWithTwoItems(
          passwordInputFieldData,
          passwordConfirmInputFieldData,
          this.props
        )}
      </Section>
    );
  };

  renderEditPasswordButton = (): Object => (
    <EditPasswordButtonWrapper>
      <Button
        onClick={() => this.onTogglePassowordDialog()}
        disableRipple
        color="primary"
        variant="contained"
        size="large"
      >
        Edit Password
      </Button>
    </EditPasswordButtonWrapper>
  );

  renderEventInfoSection = () => {
    const nameInputFieldData = getRowItemObject(
      "Title",
      "Type the title of the event...",
      "text",
      "name"
    );
    const rolInputFieldData = getRowItemObject(
      "Description",
      "Type the description of the event...",
      "textarea",
      "description"
    );

    const meetingURLInputFieldData = getRowItemObject(
      "Meeting URL ",
      "Type the meetingURL (Zoom, Google Meet, Microsoft Teams, etc...)",
      "string",
      "meetingURL"
    );
    const replayURLInputFieldData = getRowItemObject(
      "Replay URL ",
      "Type the replay URL Youtube, Vimeo, etc...)",
      "string",
      "replayURL"
    );

    const gated = isEventGated(this.props.values);
    const { linksLoadState } = this.state;

    // A gated event drops both URL fields from the payload until the stored
    // links load, so anything typed before then would be discarded on save
    // without a word. Lock the boxes instead of accepting input we will throw
    // away.
    if (gated && linksLoadState !== "loaded") {
      meetingURLInputFieldData.disabled = true;
      replayURLInputFieldData.disabled = true;
    }

    return (
      <Section>
        {renderSectionTitle("Detalles")}
        {renderRowWithTwoItems(
          nameInputFieldData,
          rolInputFieldData,
          this.props
        )}
        {gated && (
          <GatedNotice>
            Este evento es de acceso restringido. Los enlaces se guardan en
            privado y no quedan en el evento público.
          </GatedNotice>
        )}
        {gated && linksLoadState === "loading" && (
          <LinksLoadNotice>Cargando los enlaces privados.</LinksLoadNotice>
        )}
        {gated && linksLoadState === "failed" && (
          <LinksLoadNotice>
            No se pudieron cargar los enlaces privados. Si guardas ahora, los
            enlaces guardados no cambian.
          </LinksLoadNotice>
        )}
        {renderRowWithTwoItems(
          meetingURLInputFieldData,
          replayURLInputFieldData,
          this.props
        )}
      </Section>
    );
  };

  setActiveDayOfWeek = (index) => {
    const { values, setFieldValue } = this.props;
    const daysOfWeek = values["recurringDate"]["daysOfWeek"].map(
      (dayOfWeek, _index) => {
        if (index === _index) {
          return { ...dayOfWeek, isActive: !dayOfWeek.isActive };
        }

        return dayOfWeek;
      }
    );

    setFieldValue("recurringDate", {
      ...values["recurringDate"],
      daysOfWeek,
    });
  };

  setDayOfWeekTime = (index, time) => {
    const { values, setFieldValue } = this.props;
    const daysOfWeek = values["recurringDate"]["daysOfWeek"].map(
      (dayOfWeek, _index) => {
        if (index === _index) {
          return { ...dayOfWeek, time };
        }

        return dayOfWeek;
      }
    );

    setFieldValue("recurringDate", {
      ...values["recurringDate"],
      daysOfWeek,
    });
  };
  renderDatesSection = () => {
    const { isRecurringEvent } = this.state;

    const { values, setFieldValue } = this.props;

    const { recurringDate } = values;
    return (
      <Section>
        {renderSectionTitle("Dates")}
        <Row>
          <RowItem>
            <Checkbox
              type={"checkbox"}
              value={recurringDate.isActive}
              onChange={() =>
                setFieldValue("recurringDate", {
                  ...values["recurringDate"],
                  isActive: !values["recurringDate"].isActive,
                })
              }
              label={"It's a recurring event?"}
            />
          </RowItem>
        </Row>
        {!!recurringDate.isActive ? (
          <div>
            <DaysOfWeek
              days={recurringDate.daysOfWeek}
              setDays={this.setActiveDayOfWeek}
              setTime={this.setDayOfWeekTime}
            ></DaysOfWeek>
          </div>
        ) : (
          <div>
            <DatePicker
              name="Start Time & Date"
              id={"startTime"}
              value={coerceToDate(this.props.values["startTime"]) }
              //  selected={this.props.values["startTime"]}
              onChange={(date) => {
                this.props.setFieldValue("startTime", date);
              }}
            />
            <DatePicker
              name="End Time & Date"
              value={coerceToDate(this.props.values["endTime"]) }
              // selected={this.props.values["endTime"]}
              onChange={(date) => this.props.setFieldValue("endTime", date)}
            />
          </div>
        )}
        {/* The zone is not an extra. It decides what every member reads, so it
            sits with the dates and never behind a toggle. The times above are
            read as this zone's clock and stored as UTC. */}
        {renderRowWithSelect(
          getRowItemObject("Time zone", "", "select", "timezone"),
          timeZoneOptionsFor((this.props as any).values?.timezone),
          this.props as any
        )}
      </Section>
    );
  };
  renderImageSection = () => (
    <Section>
      {renderSectionTitle("Cover Image")}

      {this.renderImageField()}
    </Section>
  );
  renderImageField = () => {
    const coverInputFieldData = getRowItemObject(
      "",
      "Selecciona una imagen de portada para mostrar",
      "file",
      "coverURL"
    );
    return (
      <Field name="coverURL">
        {({ field, form }) => (
          <ImageUpload
            field={field}
            form={form}
            actionId={this.props.values?.id}
            reference={DB_PATH.EVENT}
          />
        )}
      </Field>
    );
  };
  render() {
    const {
      onChageFormToEditMode,
      onRemoveItem,
      handleSubmit,
      isSubmitting,
      item,
      mode,
    } = this.props;

    return (
      <Wrapper>
        <Form>
          {this.renderEventInfoSection()}
          {this.renderDatesSection()}
          {this.renderImageSection()}

          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={this.props.handleSubmit}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Event"
            canBeRemoved
            mode={mode}
          />
        </Form>
      </Wrapper>
    );
  }
}

const CustomForm = withFormik({
  mapPropsToValues: (props: any): EventFormValues => {
    const { item, communityId, mode } = props;
    const timezone = storedFormTimeZone(item.timezone);
    // The pickers work in browser wall time. Show the stored instant as the
    // clock the coach set it on, so reopening an event does not silently move
    // it into the editor's zone. An event with no stored zone opens in browser
    // wall time, exactly as it did before this field existed.
    const startTime = timezone
      ? utcIsoToZonedWallTime(item.startTime, timezone)
      : coerceToDate(item.startTime);
    const endTime = timezone
      ? utcIsoToZonedWallTime(item.endTime, timezone)
      : coerceToDate(item.endTime);

    return {
      timezone,
      id: item.id || "",
      coverURL: item.coverURL || "",
      logoURL: item.logoURL || "",
      name: item.name || "",
      description: item.description || "",
      meetingURL: item.meetingURL || "",
      image: null,
      category: item.category || { id: "", name: "" },
      categoryId: item.categoryId || "",
      startTime: startTime || null,
      endTime: endTime || null,
      createdAt: item.createdAt || 0,
      price: item.price || 0,
      participants: item.participants || 0,
      communityId: communityId || "",
      replayURL: item.replayURL || "",
      // Read only. Gating is authored through the MCP; the form shows it so the
      // admin knows the links are stored privately, and never writes it back.
      requiredEntitlementIds: item.requiredEntitlementIds || [],
      hasProtectedMeetingURL: Boolean(item.hasProtectedMeetingURL),
      hasProtectedReplayURL: Boolean(item.hasProtectedReplayURL),
      // A gated event has to fetch its links before the URL inputs hold
      // anything real. Everything else has nothing to load.
      protectedLinksLoaded: !(item.id && isEventGated(item)),
      categoryName: item.categoryName || "",
      reviews: item.reviews || 0,
      recurringDate: item.recurringDate || {
        isActive: false,
        daysOfWeek: [
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.MON },
          { isActive: true, time: 0, dayOfWeek: DayOfWeek.TUE },
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.WED },
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.THU },
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.FRI },
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.SAT },
          { isActive: false, time: 0, dayOfWeek: DayOfWeek.SUN },
        ],
      },
    };
  },

  validationSchema: ({ startTime }) => {
    return Yup.lazy(() =>
      Yup.object().shape({
        name: Yup.string().required("El nombre es requerido."),
        description: Yup.string().required("La descripción es requerida."),
        startTime: Yup.date()
          .nullable()
          .required("Start Date is required."),
        endTime: Yup.date()
          .nullable()
          .required("End Date is required."),
        coverURL: Yup.string().required("Image is required."),
      })
    );
  },

  handleSubmit(values, { setSubmitting, props }) {
    const { onCreateItem, onEditItem, mode } = props;

    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    //@ts-ignore
    if (mode === "create")
      values = { ...values, createdAt: new Date().toISOString() };

    const startDate = coerceToDate(values["startTime"]);
    const endDate = coerceToDate(values["endTime"]);

    // The picked wall time means the chosen zone. 10:00 with "Asia/Makassar"
    // is 02:00 UTC, whatever clock the admin sits on.
    //
    // "Not set" stays not set. Writing a guess here would move the label on
    // every event that predates this field: an edit from Manila would restamp
    // a coach's 10:00 (UTC+2) session as 16:00 (UTC+8). With no zone the
    // conversion uses the browser instant, which is what this did before.
    //
    // The callable writes with update, a merge, so an absent key keeps what is
    // stored. That protects an old event from a restamp, but it also means
    // clearing a zone needs an explicit empty string. So: omit the key when the
    // event never had a zone, and send "" when the author clears one it had.
    const pickedTimeZone = storedFormTimeZone(values["timezone"]);
    const conversionTimeZone = pickedTimeZone || guessTimeZone();
    const hadTimeZone = !!storedFormTimeZone(props?.item?.timezone);
    if (pickedTimeZone) {
      values = { ...values, timezone: pickedTimeZone };
    } else if (hadTimeZone) {
      values = { ...values, timezone: "" };
    } else {
      const withoutZone = { ...values };
      delete (withoutZone as any).timezone;
      values = withoutZone;
    }

    if (startDate) {
      values = {
        ...values,
        startTime: zonedWallTimeToUtcIso(startDate, conversionTimeZone),
      };
    }
    if (endDate) {
      values = {
        ...values,
        endTime: zonedWallTimeToUtcIso(endDate, conversionTimeZone),
      };
    }

    values.categoryId = values.category.id;
    properCallback({
      ...values,
    });

    setSubmitting(false);
  },
})(UserForm);

export default CustomForm;
